import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AppLayout } from '../../layouts/AppLayout'
import { PageHeader } from '../../layouts/PageHeader'
import { useOverdueInvoiceAging } from '../../../hooks/useOverdueInvoiceAging'
import {
	applyAdvancedFilters,
	computeSelectedSnapshot,
	type AdvancedFilterState,
	type PeriodType,
	type ProcessedMonthRow,
} from '../../../utils/overdueInvoiceAging'
import { FiltersBar } from './FiltersBar'
import { ClientDetailsTable, type ClientSortKey, type KpiViewMode } from './ClientDetailsTable'
import { MonthlyTrendsChart } from './MonthlyTrendsChart'
import { TopOverdueInvoices } from './TopOverdueInvoices'
import {
	parseClientsFromSnapshot,
	topOverdueInvoicesFromSnapshot,
} from './parseSnapshot'
import { formatCurrency } from './formatters'
import { appPath } from '../../../utils/appPath'

function sortRowsChron(rows: ProcessedMonthRow[]) {
	return [...rows].sort((a, b) => {
		const ya = Number(a.invoice_year)
		const yb = Number(b.invoice_year)
		if (ya !== yb) return ya - yb
		return Number(a.invoice_month) - Number(b.invoice_month)
	})
}

/** Trends chart: this year + last calendar year; if empty, two latest years in data. */
function chartRowsCurrentAndLastYear(rows: ProcessedMonthRow[]): ProcessedMonthRow[] {
	if (rows.length === 0) return []
	const cy = new Date().getFullYear()
	const target = new Set([cy, cy - 1])
	let out = rows.filter(r => target.has(Number(r.invoice_year)))
	if (out.length === 0) {
		const distinctYears = [...new Set(rows.map(r => Number(r.invoice_year)))].sort(
			(a, b) => b - a
		)
		const fallback = new Set(distinctYears.slice(0, 2))
		out = rows.filter(r => fallback.has(Number(r.invoice_year)))
	}
	return sortRowsChron(out)
}

const SkeletonCard: React.FC<{ className?: string }> = ({ className = 'h-32' }) => (
	<div
		className={`bg-white rounded-xl border border-slate-200 shadow-md ${className} animate-pulse`}
	/>
)

const LoadingSkeleton: React.FC = () => (
	<div className="p-6 space-y-4">
		<SkeletonCard className="h-96" />
		<SkeletonCard className="h-56" />
	</div>
)

export const OverdueInvoiceAgingPage: React.FC = () => {
	const navigate = useNavigate()
	const { processedRows, loading, error, reload } = useOverdueInvoiceAging()

	const defaultsAppliedRef = useRef(false)
	const [periodType, setPeriodType] = useState<PeriodType>('monthly')
	const [selectedYears, setSelectedYears] = useState<number[]>([])
	const [selectedMonths, setSelectedMonths] = useState<number[]>([])
	const [selectedQuarter, setSelectedQuarter] = useState('all')
	const [selectedHalfYear, setSelectedHalfYear] = useState('all')
	const [yearOpen, setYearOpen] = useState(false)
	const [monthOpen, setMonthOpen] = useState(false)

	const [kpiMode, setKpiMode] = useState<KpiViewMode>('overdue')
	const [showInvoicingView, setShowInvoicingView] = useState(false)
	const [showOnlyOverdueClients, setShowOnlyOverdueClients] = useState(true)
	const [showOutstandingOnly, setShowOutstandingOnly] = useState(false)
	const [showMultipleOverdueOnly, setShowMultipleOverdueOnly] = useState(false)

	const [sort, setSort] = useState<{ by: ClientSortKey; dir: 'asc' | 'desc' }>({
		by: 'display_amount',
		dir: 'desc',
	})

	useEffect(() => {
		if (processedRows.length === 0 || defaultsAppliedRef.current) return
		const sorted = sortRowsChron(processedRows)
		const last = sorted[sorted.length - 1]
		if (last) {
			setSelectedYears([Number(last.invoice_year)])
			setSelectedMonths([Number(last.invoice_month)])
		}
		defaultsAppliedRef.current = true
	}, [processedRows])

	const filterState: AdvancedFilterState = useMemo(
		() => ({
			year: 'all',
			years: selectedYears,
			months: selectedMonths,
			clientTier: 'all',
			riskLevel: 'all',
			dateRange: { start: null, end: null },
		}),
		[selectedYears, selectedMonths]
	)

	const filteredRows = useMemo(
		() => applyAdvancedFilters(processedRows, filterState),
		[processedRows, filterState]
	)

	const snapshot = useMemo(
		() =>
			computeSelectedSnapshot(
				filteredRows,
				periodType,
				selectedQuarter,
				selectedHalfYear,
				filterState
			),
		[filteredRows, periodType, selectedQuarter, selectedHalfYear, filterState]
	)

	const trendRows = useMemo(
		() => chartRowsCurrentAndLastYear(processedRows),
		[processedRows]
	)
	const chartYearLabel = useMemo(() => {
		if (trendRows.length === 0) return ''
		const ys = [...new Set(trendRows.map(r => Number(r.invoice_year)))].sort((a, b) => a - b)
		if (ys.length === 0) return ''
		return ys.length === 1 ? String(ys[0]) : `${ys[0]}–${ys[ys.length - 1]}`
	}, [trendRows])
	const tableClients = useMemo(() => parseClientsFromSnapshot(snapshot), [snapshot])
	const topInvoices = useMemo(() => topOverdueInvoicesFromSnapshot(snapshot), [snapshot])

	const clientsWithOverdue = useMemo(() => {
		return tableClients.filter(
			c =>
				(Number(c.overdue_invoice_count) || Number(c.overdue_invoices) || 0) > 0 ||
				Number(c.overdue_amount) > 0
		).length
	}, [tableClients])

	const effectiveOverdueInvoices = snapshot
		? Number(snapshot.overdue_invoices || snapshot.month_overdue_invoices || 0)
		: 0
	const effectiveOverdueAmount = snapshot
		? Number(snapshot.overdue_amount || snapshot.month_overdue_amount || 0)
		: 0

	const handleKpiMode = useCallback((m: KpiViewMode) => {
		setKpiMode(m)
		if (m === 'overdue') {
			setShowInvoicingView(false)
			setShowOutstandingOnly(false)
			setShowOnlyOverdueClients(true)
		} else if (m === 'invoicing') {
			setShowInvoicingView(true)
			setShowOutstandingOnly(false)
			setShowOnlyOverdueClients(false)
		} else {
			setShowInvoicingView(false)
			setShowOutstandingOnly(true)
			setShowOnlyOverdueClients(false)
		}
	}, [])

	const handleSort = useCallback((key: ClientSortKey) => {
		setSort(s => {
			if (s.by === key) {
				return { by: key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
			}
			return { by: key, dir: key === 'client_name' ? 'asc' : 'desc' }
		})
	}, [])

	const handleClientClick = useCallback(
		(id: string | number, _name: string) => {
			navigate(
				appPath(
					`/client-management?clientId=${encodeURIComponent(String(id))}`,
				),
			)
		},
		[navigate]
	)

	const handleClearAll = useCallback(() => {
		setSelectedYears([])
		setSelectedMonths([])
		setSelectedQuarter('all')
		setSelectedHalfYear('all')
		setPeriodType('monthly')
		setYearOpen(false)
		setMonthOpen(false)
	}, [])

	const toggleYear = useCallback((y: number) => {
		setSelectedYears(prev => {
			if (prev.includes(y)) return prev.filter(x => x !== y)
			return [...prev, y].sort((a, b) => b - a)
		})
		setSelectedMonths([])
	}, [])

	const toggleMonth = useCallback((m: number) => {
		setSelectedMonths(prev => {
			if (prev.includes(m)) return prev.filter(x => x !== m)
			return [...prev, m].sort((a, b) => a - b)
		})
	}, [])

	const periodLabel =
		snapshot &&
		(snapshot.month_label
			? String(snapshot.month_label)
			: `${snapshot.invoice_year}-${snapshot.invoice_month}`)

	const subtitle =
		!loading && snapshot
			? [
					periodLabel,
					`Overdue ${formatCurrency(effectiveOverdueAmount)} · ${effectiveOverdueInvoices} invoices`,
					`${clientsWithOverdue} clients with overdue`,
				]
					.filter(Boolean)
					.join(' · ')
			: undefined

	const clientCountLabel = `${filteredRows.length} period row(s) · ${tableClients.length} clients in snapshot`

	const headerFilters = !error ? (
		<FiltersBar
			sourceRows={processedRows}
			periodType={periodType}
			onPeriodType={setPeriodType}
			selectedYears={selectedYears}
			onToggleYear={toggleYear}
			onClearYears={() => {
				setSelectedYears([])
				setSelectedMonths([])
			}}
			selectedMonths={selectedMonths}
			onToggleMonth={toggleMonth}
			onClearMonths={() => setSelectedMonths([])}
			selectedQuarter={selectedQuarter}
			onQuarter={setSelectedQuarter}
			selectedHalfYear={selectedHalfYear}
			onHalfYear={setSelectedHalfYear}
			onClearAll={handleClearAll}
			yearOpen={yearOpen}
			onYearOpen={setYearOpen}
			monthOpen={monthOpen}
			onMonthOpen={setMonthOpen}
			clientCountLabel={clientCountLabel}
		/>
	) : undefined

	if (error) {
		return (
			<AppLayout>
				<PageHeader title="Overdue invoice aging" />
				<div className="p-6 flex flex-col items-center justify-center min-h-[40vh] gap-2">
					<p className="text-red-600 font-medium">Failed to load data</p>
					<p className="text-slate-600 text-sm">{error}</p>
					<button
						type="button"
						onClick={() => reload()}
						className="text-sm font-semibold text-blue-700 hover:underline"
					>
						Retry
					</button>
				</div>
			</AppLayout>
		)
	}

	return (
		<AppLayout>
			<PageHeader
				title="Overdue invoice aging"
				subtitle={subtitle}
				filters={headerFilters}
			/>

			{loading ? (
				<LoadingSkeleton />
			) : (
				<div className="p-6 space-y-4">
					{!snapshot && (
						<div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/80 p-6 text-center text-sm text-slate-500">
							No data for the current filters. Adjust period or filters.
						</div>
					)}

					{topInvoices.length > 0 && <TopOverdueInvoices invoices={topInvoices} />}

					<ClientDetailsTable
						clients={tableClients}
						mode={kpiMode}
						onModeChange={handleKpiMode}
						snapshot={snapshot}
						effectiveOverdueInvoices={effectiveOverdueInvoices}
						effectiveOverdueAmount={effectiveOverdueAmount}
						clientsWithOverdue={clientsWithOverdue}
						showInvoicingView={showInvoicingView}
						showOnlyOverdueClients={showOnlyOverdueClients}
						showOutstandingOnly={showOutstandingOnly}
						showMultipleOverdueOnly={showMultipleOverdueOnly}
						onMultipleOverdueOnlyChange={setShowMultipleOverdueOnly}
						sort={sort}
						onSort={handleSort}
						onClientClick={handleClientClick}
					/>

					<MonthlyTrendsChart rows={trendRows} yearRangeLabel={chartYearLabel} />
				</div>
			)}
		</AppLayout>
	)
}

export default OverdueInvoiceAgingPage
